import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

export interface SurveyReportWorkflowStatus {
  workflow_type: string;
  current_state: string;
  available_transitions: string[];
  workflow_metadata: Record<string, any>;
  created_on: string;
  updated_on: string;
  created_by: string;
  last_updated_by: string;
}

export interface SurveyReportWorkflowTransitionResponse {
  workflow_type: string;
  previous_state: string;
  current_state: string;
  transition_name: string;
  workflow_metadata: Record<string, any>;
  updated_on: string;
  last_updated_by: string;
}

export interface GenerateSurveyReportResponse {
  workflow_id: string;
  school_id: string;
  eoa_id: string;
  previous_state: string;
  current_state: string;
  event_id: string;
  message: string;
}

export interface FinalizedSurveyReport {
  data_file_id: string;
  upload_datetime: string;
  original_filename: string;
  file_size_bytes: number;
  file_type: string;
  file_status: string;
  download_url: string;
}

export interface GeneratedSurveyReportResponse {
  id: string;
  school_id: string;
  eoa_id: string;
  report_type: string;
  presentation_id: string;
  presentation_name: string;
  google_slides_url: string;
  pdf_download_url: string;
  created_on: string;
  generated_at: string | null;
  created_by: string;
  is_latest: boolean;
}

const getSurveyReportWorkflowStatus = async (
  eoaId: string,
  schoolId: string,
  access_token: string
): Promise<SurveyReportWorkflowStatus> => {
  const response = await axios.get<SurveyReportWorkflowStatus>(
    `${BASE_URL}/eoa/${eoaId}/workflows/survey_report/${schoolId}/status`,
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    }
  );

  return response.data;
};

const executeSurveyReportWorkflowTransition = async (
  eoaId: string,
  schoolId: string,
  access_token: string,
  transition_name: string,
  metadata?: Record<string, any>
): Promise<SurveyReportWorkflowTransitionResponse> => {
  const requestBody: any = { transition_name };
  if (metadata) {
    requestBody.workflow_metadata = metadata;
  }

  const response = await axios.post<SurveyReportWorkflowTransitionResponse>(
    `${BASE_URL}/eoa/${eoaId}/workflows/survey_report/${schoolId}/transitions`,
    requestBody,
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  return response.data;
};

const generateSurveyReport = async (
  eoaId: string,
  schoolId: string,
  access_token: string
): Promise<GenerateSurveyReportResponse> => {
  const response = await axios.post<GenerateSurveyReportResponse>(
    `${BASE_URL}/eoa/${eoaId}/schools/${schoolId}/generate_survey_report`,
    {},
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  return response.data;
};

const getGeneratedSurveyReport = async (
  eoaId: string,
  schoolId: string,
  access_token: string
): Promise<GeneratedSurveyReportResponse> => {
  const response = await axios.get<GeneratedSurveyReportResponse>(
    `${BASE_URL}/eoa/${eoaId}/schools/${schoolId}/survey_report/generated`,
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    }
  );
  return response.data;
};

const getFinalizedSurveyReport = async (
  eoaId: string,
  schoolId: string,
  access_token: string
): Promise<FinalizedSurveyReport> => {
  const response = await axios.get<FinalizedSurveyReport>(
    `${BASE_URL}/eoa/${eoaId}/schools/${schoolId}/survey_report/finalized`,
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    }
  );

  return response.data;
};

export {
  getSurveyReportWorkflowStatus,
  executeSurveyReportWorkflowTransition,
  generateSurveyReport,
  getFinalizedSurveyReport,
  getGeneratedSurveyReport,
};
